/* global gdAlertify */
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../config/axiosConfig";
import GdTable from "./GdTable";
import { formatDate } from "../../utils/utils";

const VacacionesModal = ({ isOpen, onClose, auxiliarID, nombreAuxiliar }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        fecha_inicio: "",
        fecha_fin: "",
        observacion: ""
    });

    const loadHistory = useCallback(async () => {
        if (!auxiliarID) return;
        setLoading(true);
        try {
            const response = await axiosInstance.get("/auxiliar/vacaciones/lista", {
                params: { auxiliarID }
            });
            setHistory(response.data.data || []);
        } catch (error) {
            console.error("Error al cargar historial de vacaciones:", error);
            gdAlertify.notiError("Error al cargar historial de vacaciones");
        } finally {
            setLoading(false);
        }
    }, [auxiliarID]);

    useEffect(() => {
        if (isOpen && auxiliarID) {
            loadHistory();
            setFormData({
                fecha_inicio: "",
                fecha_fin: "",
                observacion: ""
            });
        }
    }, [isOpen, auxiliarID, loadHistory]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.fecha_inicio || !formData.fecha_fin) {
            gdAlertify.notiError("Debe ingresar fecha de inicio y fin");
            return;
        }

        try {
            const response = await axiosInstance.post("/auxiliar/vacaciones/guardar", {
                ...formData,
                auxiliar_id: auxiliarID
            });

            if (response.data.error) {
                gdAlertify.notiError(response.data.message);
            } else {
                gdAlertify.notiSuccess("Vacaciones registradas correctamente");
                setFormData({
                    fecha_inicio: "",
                    fecha_fin: "",
                    observacion: ""
                });
                loadHistory();
            }
        } catch (error) {
            console.error("Error al guardar vacaciones:", error);
            gdAlertify.notiError("Error al guardar vacaciones");
        }
    };

    const handleDelete = (id) => {
        gdAlertify.confirm({
            title: "Eliminar Registro",
            message: "¿Está seguro de eliminar este registro de vacaciones?",
            okLabel: "Confirmar",
            cancelLabel: "Cancelar",
            onConfirm: async () => {
                try {
                    const response = await axiosInstance.post(`/auxiliar/vacaciones/eliminar/${id}`);
                    if (response.data.error) {
                        gdAlertify.notiError(response.data.message);
                    } else {
                        gdAlertify.notiSuccess("Registro eliminado");
                        loadHistory();
                    }
                } catch (error) {
                    console.error("Error al eliminar vacaciones:", error);
                    gdAlertify.notiError("Error al eliminar vacaciones");
                }
            }
        });
    };

    const columns = [
        { name: "fecha_inicio", title: "Desde", render: (row) => formatDate(row.fecha_inicio), width: 120 },
        { name: "fecha_fin", title: "Hasta", render: (row) => formatDate(row.fecha_fin), width: 120 },
        { name: "observacion", title: "Observación", width: 300 },
        {
            name: "id",
            title: "Acción",
            render: (row) => (
                <button 
                    className="btn btn-sm btn-danger" 
                    onClick={() => handleDelete(row.id)}
                    title="Eliminar"
                >
                    <i className="bi bi-trash"></i>
                </button>
            ),
            width: 80
        }
    ];

    if (!isOpen) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-success text-white">
                        <h5 className="modal-title">
                            <i className="bi bi-calendar-check me-2"></i>
                            Vacaciones: {nombreAuxiliar}
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {/* Formulario para agregar */}
                        <form onSubmit={handleSave} className="card p-3 mb-4 bg-light border-0">
                            <h6 className="mb-3 text-success fw-bold">Registrar Nuevas Vacaciones</h6>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Fecha Inicio</label>
                                    <input 
                                        type="date" 
                                        className="form-control form-control-sm" 
                                        name="fecha_inicio"
                                        value={formData.fecha_inicio}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Fecha Fin</label>
                                    <input 
                                        type="date" 
                                        className="form-control form-control-sm" 
                                        name="fecha_fin"
                                        value={formData.fecha_fin}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-4 d-flex align-items-end">
                                    <button type="submit" className="btn btn-sm btn-success w-100">
                                        <i className="bi bi-plus-circle me-1"></i> Guardar
                                    </button>
                                </div>
                                <div className="col-12 text-muted">
                                    <label className="form-label small fw-bold">Observación (Opcional)</label>
                                    <textarea 
                                        className="form-control form-control-sm" 
                                        rows="2" 
                                        name="observacion"
                                        value={formData.observacion}
                                        onChange={handleInputChange}
                                        placeholder="Ingrese algún detalle adicional..."
                                    ></textarea>
                                </div>
                            </div>
                        </form>

                        <hr />

                        {/* Historial */}
                        <h6 className="mb-3 fw-bold border-bottom pb-2">Historial de Vacaciones</h6>
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-success spinner-border-sm" role="status"></div>
                                <p className="small mt-2">Cargando historial...</p>
                            </div>
                        ) : history.length > 0 ? (
                            <GdTable
                                columns={columns}
                                data={history}
                                defaultItemsPerPage={10}
                                itemsPerPageOptions={[10, 20]}
                                exportFilename="historial-vacaciones.xlsx"
                            />
                        ) : (
                            <div className="text-center py-4 text-muted border rounded bg-white">
                                <i className="bi bi-info-circle mb-2 d-block fs-4 text-secondary"></i>
                                <p className="small mb-0">No hay registros de vacaciones para este auxiliar.</p>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VacacionesModal;
